<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-document" title="评估记录管理" subtitle="查看和管理评估记录、离职回溯与调研数据" />

    <el-tabs v-model="activeTab" class="eval-tabs" @tab-click="onTabClick">
      <!-- Tab1: 在职评估记录 -->
      <el-tab-pane label="在职评估记录" name="evaluation">
        <!-- 工具栏 -->
        <div class="toolbar">
          <el-input v-model="search" placeholder="搜索员工姓名或工号" prefix-icon="el-icon-search" clearable style="width: 260px;" />
          <el-select v-model="filterDept" placeholder="部门" clearable style="width: 150px; margin-left: 12px;">
            <el-option v-for="d in deptOptions" :key="d" :label="d" :value="d" />
          </el-select>
          <el-select v-model="filterRisk" placeholder="风险等级" clearable style="width: 140px; margin-left: 12px;">
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
          <el-button icon="el-icon-download" @click="exportEvaluations">导出Excel</el-button>
          <el-button icon="el-icon-refresh" @click="reloadEvaluations">刷新</el-button>
        </div>

        <!-- 表格 -->
        <div class="table-wrapper">
          <el-table :data="pagedList" stripe class="data-table" v-loading="loading">
            <el-table-column label="员工" min-width="90">
              <template slot-scope="scope">{{ scope.row.employee_name || '-' }}</template>
            </el-table-column>
            <el-table-column label="部门" min-width="110">
              <template slot-scope="scope">{{ scope.row.dept_name || getEmployeeInfo(scope.row.employee_id).dept_name || '-' }}</template>
            </el-table-column>
            <el-table-column label="职位" min-width="130">
              <template slot-scope="scope">{{ scope.row.job_title || getEmployeeInfo(scope.row.employee_id).job_title || '-' }}</template>
            </el-table-column>
            <el-table-column label="评估人" min-width="110">
              <template slot-scope="scope">{{ scope.row.evaluator_name || '-' }}</template>
            </el-table-column>
            <el-table-column v-if="!isTaskLayer" label="所属任务" min-width="150">
              <template slot-scope="scope">
                <el-tag size="mini" type="info" effect="plain">{{ scope.row.task_name || '未分配' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="分数" min-width="90">
              <template slot-scope="scope">
                <RiskScoreTag :score="scope.row.total_score" />
              </template>
            </el-table-column>
            <el-table-column label="风险等级" min-width="100">
              <template slot-scope="scope">
                <span class="risk-tag" :class="'risk-tag-' + scope.row.risk_level">{{ riskLabel(scope.row.risk_level) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="评估时间" min-width="150" />
            <el-table-column label="操作" min-width="200">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="viewReport(scope.row)">查看报告</el-button>
                <el-button v-if="$hasPermission('evaluation:delete')" type="text" size="small" class="action-disable" @click="remove(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination-bar">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next"
              :total="filteredList.length"
              :page-size="pageSize"
              :current-page="page"
              :page-sizes="[10, 20, 50]"
              @size-change="handleSizeChange"
              @current-change="handlePageChange"
            />
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab2: 离职回溯记录 -->
      <el-tab-pane label="离职回溯记录" name="departure">
        <div class="toolbar">
          <el-input v-model="depSearch" placeholder="搜索姓名" prefix-icon="el-icon-search" clearable style="width: 200px;" />
          <el-input v-model="depManagerSearch" placeholder="搜索主管" prefix-icon="el-icon-search" clearable style="width: 200px; margin-left: 12px;" />
          <el-date-picker
            v-model="depDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="margin-left: 12px;"
          />
          <el-button style="margin-left: auto;" icon="el-icon-download" @click="exportDeparture">导出Excel</el-button>
        </div>

        <div class="table-wrapper">
          <el-table :data="pagedDepartures" stripe class="data-table">
            <el-table-column prop="real_name" label="员工姓名" min-width="90" />
            <el-table-column label="部门" min-width="110">
              <template slot-scope="scope">{{ scope.row.dept_name || '-' }}</template>
            </el-table-column>
            <el-table-column label="职位" min-width="130">
              <template slot-scope="scope">{{ scope.row.job_title || '-' }}</template>
            </el-table-column>
            <el-table-column label="主管" min-width="140">
              <template slot-scope="scope">{{ scope.row.manager_name || scope.row.manager_email || '-' }}</template>
            </el-table-column>
            <el-table-column label="离职日期" min-width="120">
              <template slot-scope="scope">{{ scope.row.resignation_date || '-' }}</template>
            </el-table-column>
            <el-table-column label="回溯状态" min-width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.has_feedback ? 'success' : 'info'" size="mini">
                  {{ scope.row.has_feedback ? '已回溯' : '未回溯' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" min-width="140">
              <template slot-scope="scope">
                <el-button
                  type="text"
                  size="small"
                  :disabled="!scope.row.has_feedback"
                  @click="viewDepartureDetail(scope.row)"
                >查看离职回溯</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination-bar">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next"
              :total="filteredDepartures.length"
              :page-size="depPageSize"
              :current-page="depPage"
              :page-sizes="[10, 20, 50]"
              @size-change="depSizeChange"
              @current-change="depPageChange"
            />
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab3: 离职调研记录 -->
      <el-tab-pane label="离职调研记录" name="survey">
        <div class="toolbar">
          <el-input v-model="surveySearch" placeholder="搜索姓名" prefix-icon="el-icon-search" clearable style="width: 200px;" />
          <el-select v-model="surveyStatusFilter" placeholder="填写状态" clearable style="width: 140px; margin-left: 12px;">
            <el-option label="全部" value="" />
            <el-option label="未发送" value="unsent" />
            <el-option label="已发送待填写" value="pending" />
            <el-option label="已填写" value="completed" />
          </el-select>
          <el-button style="margin-left: auto;" icon="el-icon-download" @click="exportSurveys">导出Excel</el-button>
        </div>

        <div class="table-wrapper">
          <el-table :data="pagedSurveyRecords" stripe class="data-table">
            <el-table-column prop="employee_name" label="员工姓名" min-width="90" />
            <el-table-column label="部门" min-width="110">
              <template slot-scope="scope">{{ scope.row.dept_name || '-' }}</template>
            </el-table-column>
            <el-table-column label="职位" min-width="130">
              <template slot-scope="scope">{{ scope.row.job_title || '-' }}</template>
            </el-table-column>
            <el-table-column label="发送时间" min-width="160">
              <template slot-scope="scope">{{ scope.row.created_at || '-' }}</template>
            </el-table-column>
            <el-table-column label="填写状态" min-width="110">
              <template slot-scope="scope">
                <el-tag :type="surveyStatusTag(scope.row.status)" size="mini">{{ surveyStatusLabel(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="填写时间" min-width="160">
              <template slot-scope="scope">{{ scope.row.submitted_at || '-' }}</template>
            </el-table-column>
            <el-table-column label="操作" min-width="140">
              <template slot-scope="scope">
                <el-button
                  type="text"
                  size="small"
                  :disabled="scope.row.status !== 'completed'"
                  @click="viewSurveyResult(scope.row)"
                >查看调研结果</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination-bar">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next"
              :total="filteredSurveyRecords.length"
              :page-size="surveyPageSize"
              :current-page="surveyPage"
              :page-sizes="[10, 20, 50]"
              @size-change="surveySizeChange"
              @current-change="surveyPageChange"
            />
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- 评估详情弹窗（Tab1） -->
    <el-dialog title="评估详情" :visible.sync="detailVisible" width="700px" :append-to-body="true" custom-class="evaluation-detail-dialog">
      <div v-if="detailRow" class="detail-body">
        <div class="detail-section">
          <h3 class="section-title"><i class="el-icon-user"></i> 员工信息</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">姓名</span>
              <span class="detail-value">{{ detailRow.employee_name || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">工号</span>
              <span class="detail-value">{{ detailRow.employee_no || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">部门</span>
              <span class="detail-value">{{ detailRow.dept_name || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">职位</span>
              <span class="detail-value">{{ detailRow.job_title || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">评估人</span>
              <span class="detail-value">{{ detailRow.evaluator_name || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">所属任务</span>
              <span class="detail-value">{{ detailRow.task_name || '-' }}</span>
            </div>
          </div>
        </div>
        <div class="detail-section">
          <h3 class="section-title"><i class="el-icon-data-line"></i> 风险概览</h3>
          <div class="risk-overview">
            <div class="risk-score-box">
              <div class="risk-score-value" :class="'risk-level-' + detailRow.risk_level">{{ detailRow.total_score }}</div>
              <div class="risk-score-label">总分</div>
            </div>
            <div class="risk-level-box">
              <div class="risk-level-value" :class="'risk-level-' + detailRow.risk_level">{{ riskLabel(detailRow.risk_level) }}</div>
              <div class="risk-level-label">风险等级</div>
            </div>
            <div class="risk-time-box">
              <div class="risk-time-value">{{ detailRow.created_at }}</div>
              <div class="risk-time-label">评估时间</div>
            </div>
          </div>
        </div>
        <div class="detail-section" v-if="detailRow.risk_analysis">
          <h3 class="section-title"><i class="el-icon-chat-line-square"></i> 风险分析</h3>
          <p class="risk-analysis-text">{{ detailRow.risk_analysis }}</p>
        </div>
        <div class="detail-section" v-if="detailRow.matched_behaviors && detailRow.matched_behaviors.length">
          <h3 class="section-title"><i class="el-icon-bell"></i> 触发的风险行为指标</h3>
          <div class="behavior-list">
            <div v-for="(b, idx) in detailRow.matched_behaviors" :key="idx" class="behavior-item">
              <span class="behavior-score">+{{ b.score }}</span>
              <span class="behavior-desc">{{ b.description }}</span>
            </div>
          </div>
        </div>
        <div class="detail-section" v-if="detailRow.matched_suggestions && detailRow.matched_suggestions.length">
          <h3 class="section-title"><i class="el-icon-lightbulb"></i> 匹配建议</h3>
          <div class="suggestion-list">
            <div v-for="(s, idx) in detailRow.matched_suggestions" :key="idx" class="suggestion-item">
              <div class="suggestion-title">{{ s.title }}</div>
              <div class="suggestion-content">{{ s.content }}</div>
            </div>
          </div>
        </div>
        <div class="detail-section" v-if="detailRow.recommended_courses && detailRow.recommended_courses.length">
          <h3 class="section-title"><i class="el-icon-reading"></i> 推荐课程</h3>
          <div class="course-list">
            <div v-for="(c, idx) in detailRow.recommended_courses" :key="idx" class="course-item">
              <div class="course-title">{{ c.title }}</div>
              <div class="course-content">{{ c.content }}</div>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" @click="detailVisible = false">确定</el-button>
      </span>
    </el-dialog>

    <!-- 离职回溯详情弹窗（Tab2） -->
    <el-dialog title="离职回溯详情" :visible.sync="depDetailVisible" width="640px" :append-to-body="true">
      <div v-if="depDetailRow" class="detail-body">
        <div class="detail-section">
          <div class="detail-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="detail-item">
              <span class="detail-label">员工姓名</span>
              <span class="detail-value">{{ depDetailRow.real_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">主管</span>
              <span class="detail-value">{{ depDetailRow.manager_name || depDetailRow.manager_email || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">离职日期</span>
              <span class="detail-value">{{ depDetailRow.resignation_date || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">回溯提交时间</span>
              <span class="detail-value">{{ depDetailRow.feedback_created_at || '-' }}</span>
            </div>
          </div>
        </div>

        <div v-if="depDetailRow.behavior_details" class="detail-section">
          <h3 class="section-title"><i class="el-icon-document"></i> 补充说明</h3>
          <p class="risk-analysis-text">{{ depDetailRow.behavior_details }}</p>
        </div>

        <div v-for="group in feedbackBehaviorGroups" :key="group.category_id" class="detail-section">
          <h3 class="section-title">{{ group.category_name }}</h3>
          <div class="behavior-check-list">
            <div v-for="b in group.items" :key="b.behavior_id" class="behavior-check-item">
              <span class="behavior-check-desc">{{ b.description }}</span>
              <el-tag :type="b.confirmed ? 'success' : 'info'" size="mini">
                {{ b.confirmed ? '是' : '否' }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="depDetailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 调研结果详情弹窗（Tab3） -->
    <el-dialog title="调研结果详情" :visible.sync="surveyDetailVisible" width="640px" :append-to-body="true">
      <div v-if="surveyDetailRow" class="detail-body">
        <div class="detail-section">
          <div class="detail-grid" style="grid-template-columns: repeat(3, 1fr);">
            <div class="detail-item">
              <span class="detail-label">员工姓名</span>
              <span class="detail-value">{{ surveyDetailRow.employee_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">部门</span>
              <span class="detail-value">{{ surveyDetailRow.dept_name || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">职位</span>
              <span class="detail-value">{{ surveyDetailRow.job_title || '-' }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="detail-item">
              <span class="detail-label">链接发送时间</span>
              <span class="detail-value">{{ surveyDetailRow.created_at || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">填写提交时间</span>
              <span class="detail-value">{{ surveyDetailRow.submitted_at || '-' }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title"><i class="el-icon-document-checked"></i> 填写内容</h3>
          <div v-for="(qa, i) in surveyQAList" :key="i" class="survey-qa-item">
            <div class="survey-qa-q">{{ i + 1 }}. {{ qa.question_text }} {{ qa.required ? '*' : '' }}</div>
            <div class="survey-qa-a">
              <template v-if="qa.question_type === 'multiple_choice'">
                <el-tag v-for="(opt, j) in (qa.answer || [])" :key="j" size="mini" style="margin: 2px;">{{ opt }}</el-tag>
                <span v-if="!qa.answer || qa.answer.length === 0" style="color: var(--color-text-muted);">未填写</span>
              </template>
              <template v-else-if="qa.question_type === 'single_choice'">
                <span v-if="qa.answer">{{ qa.answer }}</span>
                <span v-else style="color: var(--color-text-muted);">未填写</span>
              </template>
              <template v-else>
                <p v-if="qa.answer" class="risk-analysis-text">{{ qa.answer }}</p>
                <span v-else style="color: var(--color-text-muted);">未填写</span>
              </template>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="surveyDetailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import RiskScoreTag from '../../components/RiskScoreTag.vue'
import { filterByPinyin } from '../../utils/pinyinFilter'
import { filterByDataScope } from '../../utils/permission'

export default {
  name: 'EvaluationManage',
  components: { PageHeader, RiskScoreTag },
  data() {
    return {
      activeTab: 'evaluation',
      tabLoaded: { evaluation: false, departure: false, survey: false },
      // Tab1
      search: '',
      filterRisk: null,
      filterDept: '',
      page: 1,
      pageSize: 10,
      loading: false,
      detailVisible: false,
      detailRow: null,
      // Tab2
      depSearch: '',
      depManagerSearch: '',
      depDateRange: null,
      depPage: 1,
      depPageSize: 10,
      depDetailVisible: false,
      depDetailRow: null,
      // Tab3
      surveySearch: '',
      surveyStatusFilter: '',
      surveyPage: 1,
      surveyPageSize: 10,
      surveyDetailVisible: false,
      surveyDetailRow: null
    }
  },
  computed: {
    // Tab1
    list() {
      const taskId = this.$store.getters.currentTaskId
      const all = this.$store.state.evaluations || []
      if (!taskId) return all
      return all.filter(e => String(e.task_id) === String(taskId))
    },
    employees() {
      const taskId = this.$store.getters.currentTaskId
      const all = (this.$store.state.employees && this.$store.state.employees.employees) || []
      if (!taskId) return all
      return all.filter(e => e.task_id === taskId || !e.task_id)
    },
    isTaskLayer() {
      return this.$route.path.includes('/admin/tasks/')
    },
    deptOptions() {
      const set = new Set()
      this.list.forEach(e => {
        const dept = e.dept_name || this.getEmployeeInfo(e.employee_id).dept_name
        if (dept) set.add(dept)
      })
      return Array.from(set).sort()
    },
    filteredList() {
      let data = [...this.list]
      if (this.search) {
        const k = this.search.toLowerCase().trim()
        const byPinyin = filterByPinyin(data, this.search, 'employee_name')
        if (byPinyin.length > 0 && this.search.length >= 2) {
          data = byPinyin
        } else {
          data = data.filter(e => {
            const nameMatch = (e.employee_name || '').toLowerCase().includes(k)
            const noMatch = (e.employee_no || '').toLowerCase().includes(k)
            return nameMatch || noMatch
          })
        }
      }
      if (this.filterDept) {
        data = data.filter(e => {
          const dept = e.dept_name || this.getEmployeeInfo(e.employee_id).dept_name || ''
          return dept === this.filterDept
        })
      }
      // 排除自评记录（主管不应评估自己）
      data = data.filter(e => {
        if (e.evaluator_id != null && e.employee_id != null) {
          return String(e.evaluator_id) !== String(e.employee_id)
        }
        const evalName = (e.evaluator_name || '').replace(/[（(]自评[）)]/g, '').trim()
        return evalName !== (e.employee_name || '').trim()
      })
      if (this.filterRisk) {
        data = data.filter(e => e.risk_level === this.filterRisk)
      }
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      return filterByDataScope(this.$store, data, { nameField: 'employee_name' })
    },
    pagedList() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredList.slice(start, start + this.pageSize)
    },

    // Tab2
    resignedEmployees() {
      const taskId = this.$store.getters.currentTaskId
      const all = (this.$store.state.employees && this.$store.state.employees.resignedEmployees) || []
      let list = !taskId ? [...all] : all.filter(e => e.task_id === taskId || !e.task_id)
      // 关联员工信息（部门、职位）
      const emps = this.employees
      return list.map(e => {
        const emp = emps.find(emp => emp.employee_id === e.employee_id)
        return { ...e, dept_name: e.dept_name || (emp && emp.dept_name) || '', job_title: e.job_title || (emp && emp.job_title) || '' }
      })
    },
    departureFeedbacks() {
      const taskId = this.$store.getters.currentTaskId
      const all = (this.$store.state.departureFeedbacks && this.$store.state.departureFeedbacks.feedbacks) || []
      return !taskId ? all : all.filter(f => f.task_id === taskId || !f.task_id)
    },
    departureList() {
      const emps = this.employees
      return this.resignedEmployees.map(e => {
        // 找该员工的feedback
        const fbList = this.departureFeedbacks.filter(f => f.employee_id === e.employee_id)
        const latestFb = fbList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
        const manager = emps.find(emp => emp.email === (latestFb && latestFb.manager_email)) || emps.find(emp => emp.email === e.resigned_manager_email)
        return {
          ...e,
          has_feedback: latestFb && latestFb.source === 'completed',
          feedback_id: latestFb ? latestFb.feedback_id : null,
          feedback_created_at: latestFb ? latestFb.created_at : null,
          manager_name: manager ? manager.real_name : (latestFb ? latestFb.manager_email : e.resigned_manager_email || e.manager_email || '-'),
          manager_email: latestFb ? latestFb.manager_email : (e.resigned_manager_email || e.manager_email || ''),
          observed_behaviors: latestFb ? latestFb.observed_behaviors : [],
          behavior_details: latestFb ? latestFb.behavior_details : ''
        }
      })
    },
    filteredDepartures() {
      let data = [...this.departureList]
      if (this.depSearch) {
        const k = this.depSearch.toLowerCase()
        data = data.filter(d => (d.real_name || '').toLowerCase().includes(k))
      }
      if (this.depManagerSearch) {
        const k = this.depManagerSearch.toLowerCase()
        data = data.filter(d => (d.manager_name || '').toLowerCase().includes(k) || (d.manager_email || '').toLowerCase().includes(k))
      }
      if (this.depDateRange && this.depDateRange.length === 2) {
        const start = new Date(this.depDateRange[0]).getTime()
        const end = new Date(this.depDateRange[1]).getTime()
        data = data.filter(d => {
          const t = new Date(d.resignation_date).getTime()
          return t >= start && t <= end
        })
      }
      return data
    },
    pagedDepartures() {
      const start = (this.depPage - 1) * this.depPageSize
      return this.filteredDepartures.slice(start, start + this.depPageSize)
    },
    feedbackBehaviorGroups() {
      if (!this.depDetailRow) return []
      const categories = this.$store.state.behaviors.categories || []
      const behaviors = this.$store.state.behaviors.behaviors || []
      const confirmedIds = this.depDetailRow.observed_behaviors || []
      const groups = []
      for (const cat of categories) {
        const items = behaviors.filter(b => b.category_id === cat.category_id)
        if (items.length) {
          groups.push({
            category_id: cat.category_id,
            category_name: cat.category_name,
            items: items.map(b => ({ ...b, confirmed: confirmedIds.includes(b.behavior_id) }))
          })
        }
      }
      const uncategorized = behaviors.filter(b => !categories.some(c => c.category_id === b.category_id))
      if (uncategorized.length) {
        groups.push({
          category_id: -1,
          category_name: '其他',
          items: uncategorized.map(b => ({ ...b, confirmed: confirmedIds.includes(b.behavior_id) }))
        })
      }
      return groups
    },

    // Tab3
    surveys() {
      const taskId = this.$store.getters.currentTaskId
      const all = (this.$store.state.surveys && this.$store.state.surveys.surveys) || []
      const responses = (this.$store.state.surveys && this.$store.state.surveys.responses) || []
      let list = !taskId ? all : all.filter(s => s.task_id === taskId || !s.task_id)
      return list.map(s => {
        const resp = responses.find(r => r.survey_id === s.survey_id || r.survey_token === s.token)
        // 找员工信息
        const emp = this.employees.find(e => e.employee_id === s.employee_id)
        return {
          ...s,
          status: resp ? 'completed' : (s.status || 'pending'),
          submitted_at: resp ? resp.submitted_at : null,
          answers: resp ? resp.answers : {},
          dept_name: emp ? emp.dept_name : '',
          job_title: emp ? emp.job_title : ''
        }
      })
    },
    filteredSurveyRecords() {
      let data = [...this.surveys]
      if (this.surveySearch) {
        const k = this.surveySearch.toLowerCase()
        data = data.filter(s => (s.employee_name || '').toLowerCase().includes(k))
      }
      if (this.surveyStatusFilter) {
        if (this.surveyStatusFilter === 'unsent') {
          // unsent 表示没有 survey 记录的情况，这里列表里只有已发送的
          data = []
        } else {
          data = data.filter(s => s.status === this.surveyStatusFilter)
        }
      }
      return data.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    },
    pagedSurveyRecords() {
      const start = (this.surveyPage - 1) * this.surveyPageSize
      return this.filteredSurveyRecords.slice(start, start + this.surveyPageSize)
    },
    surveyQuestions() {
      return this.$store.state.surveys.questions || []
    },
    surveyQAList() {
      if (!this.surveyDetailRow) return []
      const answers = this.surveyDetailRow.answers || {}
      return this.surveyQuestions.map(q => ({
        ...q,
        answer: answers[q.question_id]
      }))
    }
  },
  created() {
    this.onTabClick()
  },
  methods: {
    onTabClick() {
      if (this.activeTab === 'evaluation' && !this.tabLoaded.evaluation) {
        this.loadEvaluations()
        this.tabLoaded.evaluation = true
      } else if (this.activeTab === 'departure' && !this.tabLoaded.departure) {
        this.loadDepartureFeedbacks()
        this.tabLoaded.departure = true
      } else if (this.activeTab === 'survey' && !this.tabLoaded.survey) {
        this.loadSurveys()
        this.loadSurveyQuestions()
        this.tabLoaded.survey = true
      }
    },
    evaluationQueryParams() {
      const taskId = this.$store.getters.currentTaskId
      return taskId ? { task_id: taskId } : {}
    },
    async loadEvaluations() {
      this.loading = true
      try {
        const res = await this.$api.fetchAdminEvaluations(this.evaluationQueryParams())
        if (res.data) {
          this.$store.commit('SET_EVALUATIONS', res.data)
        }
      } catch (e) {}
      this.loading = false
    },
    async loadDepartureFeedbacks() {
      try {
        const res = await this.$api.fetchDepartureFeedbacks()
        if (res.data && res.data.code === 200) {
          this.$store.commit('SET_FEEDBACKS', res.data.data || [])
        }
      } catch (e) {}
    },
    async loadSurveys() {
      try {
        const res = await this.$api.fetchSurveys()
        if (res.data && res.data.code === 200) {
          this.$store.commit('SET_SURVEYS', res.data.data || [])
        }
        const rRes = await this.$api.fetchSurveyResponses()
        if (rRes.data && rRes.data.code === 200) {
          this.$store.commit('SET_RESPONSES', rRes.data.data || [])
        }
      } catch (e) {}
    },
    async loadSurveyQuestions() {
      try {
        const res = await this.$api.fetchSurveyQuestions()
        if (res.data && res.data.code === 200) {
          this.$store.commit('SET_SURVEY_QUESTIONS', res.data.data || [])
        }
      } catch (e) {}
    },
    riskLabel(level) {
      const map = { 1: '低风险', 2: '中风险', 3: '高风险' }
      return map[level] || '未知'
    },
    getEmployeeInfo(employeeId) {
      const emp = this.employees.find(e => e.employee_id === employeeId)
      return emp || {}
    },
    handleSizeChange(size) { this.pageSize = size; this.page = 1 },
    handlePageChange(page) { this.page = page },
    viewReport(row) {
      this.detailRow = row
      this.detailVisible = true
    },
    async reloadEvaluations() {
      this.loading = true
      try {
        const res = await this.$api.fetchAdminEvaluations(this.evaluationQueryParams())
        if (res.data) {
          this.$store.commit('SET_EVALUATIONS', res.data)
          this.$message.success('已刷新')
        }
      } catch (e) {
        this.$message.error('刷新失败：' + (e.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    async remove(row) {
      try {
        await this.$confirm('确定删除此评估记录？删除后不可恢复。', '提示', { type: 'warning' })
        await this.$api.deleteEvaluation(row.evaluation_id)
        const res = await this.$api.fetchAdminEvaluations(this.evaluationQueryParams())
        if (res.data) {
          this.$store.commit('SET_EVALUATIONS', res.data)
        }
        this.$message.success('已删除')
      } catch (e) {}
    },

    // Tab2
    depSizeChange(size) { this.depPageSize = size; this.depPage = 1 },
    depPageChange(page) { this.depPage = page },
    viewDepartureDetail(row) {
      this.depDetailRow = row
      this.depDetailVisible = true
    },
    exportEvaluations() {
      const headers = ['员工', '部门', '职位', '评估人', '所属任务', '分数', '风险等级', '评估时间']
      const rows = this.filteredList.map(e => [
        e.employee_name || '-',
        e.dept_name || this.getEmployeeInfo(e.employee_id).dept_name || '-',
        e.job_title || this.getEmployeeInfo(e.employee_id).job_title || '-',
        e.evaluator_name || '-',
        e.task_name || '未分配',
        e.total_score != null ? e.total_score : '-',
        this.riskLabel(e.risk_level),
        e.created_at || '-'
      ])
      this.downloadCSV('在职评估记录.csv', [headers, ...rows])
    },
    exportDeparture() {
      const headers = ['员工姓名', '部门', '职位', '主管', '离职日期', '回溯状态']
      const rows = this.filteredDepartures.map(d => [
        d.real_name,
        d.dept_name || '',
        d.job_title || '',
        d.manager_name || d.manager_email || '',
        d.resignation_date || '',
        d.has_feedback ? '已回溯' : '未回溯'
      ])
      this.downloadCSV('离职回溯记录.csv', [headers, ...rows])
    },

    // Tab3
    surveySizeChange(size) { this.surveyPageSize = size; this.surveyPage = 1 },
    surveyPageChange(page) { this.surveyPage = page },
    surveyStatusLabel(status) {
      const map = { pending: '已发送待填写', completed: '已填写' }
      return map[status] || status
    },
    surveyStatusTag(status) {
      const map = { pending: 'warning', completed: 'success' }
      return map[status] || 'info'
    },
    viewSurveyResult(row) {
      this.surveyDetailRow = row
      this.surveyDetailVisible = true
    },
    exportSurveys() {
      const headers = ['员工姓名', '部门', '职位', '发送时间', '填写状态', '填写时间']
      const rows = this.filteredSurveyRecords.map(s => [
        s.employee_name,
        s.dept_name || '',
        s.job_title || '',
        s.created_at || '',
        this.surveyStatusLabel(s.status),
        s.submitted_at || ''
      ])
      this.downloadCSV('离职调研记录.csv', [headers, ...rows])
    },

    downloadCSV(filename, rows) {
      let csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
    }
  }
}
</script>

<style scoped>
.eval-tabs >>> .el-tabs__header {
  padding: 0;
  margin-bottom: var(--space-lg);
  border-bottom: 1px solid var(--color-border);
}
.eval-tabs >>> .el-tabs__nav-wrap::after { background: transparent; }
.eval-tabs >>> .el-tabs__item {
  font-weight: 600;
  padding: 0 var(--space-xl);
  height: 48px;
  line-height: 48px;
}
.eval-tabs >>> .el-tabs__item.is-active { color: var(--color-primary); }
.eval-tabs >>> .el-tabs__active-bar { background: var(--color-primary); }

/* 工具栏 */
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); flex-wrap: wrap; gap: 8px; }

/* 表格 */
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }

/* 风险标签 */
.risk-tag { display: inline-block; padding: 2px 10px; border-radius: var(--radius-full); font-size: 12px; font-weight: 600; }
.risk-tag-1 { background: var(--color-low-bg); color: var(--color-low-text); }
.risk-tag-2 { background: var(--color-medium-bg); color: var(--color-medium-text); }
.risk-tag-3 { background: var(--color-high-bg); color: var(--color-high-text); }
.action-disable { color: var(--color-high) !important; }

/* 分页 */
.pagination-bar { display: flex; justify-content: flex-end; padding: var(--space-md) var(--space-lg); background: var(--color-surface); border-top: 1px solid var(--color-border); }

/* 详情弹窗 */
.detail-body { max-height: 520px; overflow-y: auto; padding-right: 8px; }
.detail-section { margin-bottom: var(--space-lg); }
.detail-section:last-child { margin-bottom: 0; }
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-md) 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-title i { color: var(--color-primary); }

/* 员工信息网格 */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 16px;
  background: var(--color-bg);
  padding: var(--space-md);
  border-radius: var(--radius-md);
}
.detail-item { display: flex; flex-direction: column; gap: 4px; }
.detail-label { font-size: 12px; color: var(--color-text-muted); }
.detail-value { font-size: 13px; font-weight: 600; color: var(--color-text); }

/* 风险概览 */
.risk-overview {
  display: flex;
  gap: var(--space-lg);
  background: var(--color-bg);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  align-items: center;
}
.risk-score-box, .risk-level-box, .risk-time-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
}
.risk-score-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}
.risk-score-value.risk-level-1 { color: var(--color-low-text); }
.risk-score-value.risk-level-2 { color: var(--color-medium-text); }
.risk-score-value.risk-level-3 { color: var(--color-high-text); }
.risk-score-label, .risk-level-label, .risk-time-label { font-size: 12px; color: var(--color-text-muted); }
.risk-level-value {
  font-size: 16px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: var(--radius-full);
}
.risk-level-value.risk-level-1 { background: var(--color-low-bg); color: var(--color-low-text); }
.risk-level-value.risk-level-2 { background: var(--color-medium-bg); color: var(--color-medium-text); }
.risk-level-value.risk-level-3 { background: var(--color-high-bg); color: var(--color-high-text); }
.risk-time-value { font-size: 14px; font-weight: 600; color: var(--color-text); }

/* 风险分析文字 */
.risk-analysis-text {
  background: var(--color-bg);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  font-size: 13px;
  color: var(--color-text);
  line-height: 1.6;
  margin: 0;
}

/* 行为指标列表 */
.behavior-list { display: flex; flex-direction: column; gap: 8px; }
.behavior-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: var(--color-bg);
  padding: 10px 12px;
  border-radius: var(--radius-md);
}
.behavior-score {
  background: var(--color-high-bg);
  color: var(--color-high-text);
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
  margin-top: 1px;
}
.behavior-desc { font-size: 13px; color: var(--color-text); line-height: 1.5; }

/* 行为回溯列表 */
.behavior-check-list { display: flex; flex-direction: column; gap: 8px; }
.behavior-check-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-bg);
  padding: 10px 12px;
  border-radius: var(--radius-md);
}
.behavior-check-desc { font-size: 13px; color: var(--color-text); flex: 1; }

/* 建议/课程列表 */
.suggestion-list, .course-list { display: flex; flex-direction: column; gap: 10px; }
.suggestion-item, .course-item {
  background: var(--color-bg);
  padding: 12px 14px;
  border-radius: var(--radius-md);
  border-left: 3px solid var(--color-primary);
}
.course-item { border-left-color: #188038; }
.suggestion-title, .course-title { font-size: 13px; font-weight: 600; color: var(--color-text); margin-bottom: 4px; }
.suggestion-content, .course-content { font-size: 12px; color: var(--color-text-secondary); line-height: 1.5; }

/* 调研QA */
.survey-qa-item { margin-bottom: var(--space-lg); }
.survey-qa-q {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}
.survey-qa-a {
  background: var(--color-bg);
  padding: var(--space-md);
  border-radius: var(--radius-md);
}
</style>
